<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormLegend from '@js/components/forms/FormLegend.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { object, string } from 'yup';

const MAX_DATES = 100;

const weekDays = [
    { value: 1, label: 'Lundi' },
    { value: 2, label: 'Mardi' },
    { value: 3, label: 'Mercredi' },
    { value: 4, label: 'Jeudi' },
    { value: 5, label: 'Vendredi' },
    { value: 6, label: 'Samedi' },
    { value: 0, label: 'Dimanche' },
];

const props = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    smoPost: {
        type: Object,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    fromDate: '',
    toDate: '',
    // A duplicate keeps the time of the post it comes from.
    time: props.smoPost.scheduledAt.slice(11, 16),
    weekDays: weekDays.reduce((accumulator, weekDay) => {
        accumulator[weekDay.value] = false;

        return accumulator;
    }, {}),
});

const validationSchema = object({
    fromDate: string().required('Ce champ est obligatoire.'),
    toDate: string().required('Ce champ est obligatoire.'),
    time: string().required('Ce champ est obligatoire.'),
});

const dates = computed(() => {
    if (!form.fromDate || !form.toDate || !form.time) {
        return [];
    }

    const selectedWeekDays = Object.keys(form.weekDays)
        .filter((weekDay) => form.weekDays[weekDay])
        .map((weekDay) => parseInt(weekDay, 10));

    if (!selectedWeekDays.length) {
        return [];
    }

    const [hours, minutes] = form.time.split(':');

    const from = new Date(form.fromDate);
    const to = new Date(form.toDate);

    if (
        Number.isNaN(from.getTime()) ||
        Number.isNaN(to.getTime()) ||
        from > to
    ) {
        return [];
    }

    const generatedDates = [];
    const cursor = new Date(from);

    while (cursor <= to && generatedDates.length < MAX_DATES) {
        if (selectedWeekDays.includes(cursor.getDay())) {
            const date = new Date(cursor);
            date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

            generatedDates.push(date);
        }

        cursor.setDate(cursor.getDate() + 1);
    }

    return generatedDates;
});

function toLocalDateTimeString(date) {
    const pad = (value) => value.toString().padStart(2, '0');

    return (
        date.getFullYear() +
        '-' +
        pad(date.getMonth() + 1) +
        '-' +
        pad(date.getDate()) +
        ' ' +
        pad(date.getHours()) +
        ':' +
        pad(date.getMinutes()) +
        ':00'
    );
}

function submit() {
    if (!dates.value.length) {
        return;
    }

    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.smo.postDuplicatePost', {
                tenant: props.tenant.id,
                smoPost: props.smoPost.id,
            }),
            {
                dates: dates.value.map((date) => toLocalDateTimeString(date)),
            },
        )
        .then(() => {
            isOpen.value = false;

            if (props.callback) {
                props.callback();
            }

            form.fromDate = '';
            form.toDate = '';
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="large">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Dupliquer la publication </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <FormLegend>
                            Le contenu et les visuels de la publication seront
                            recopiés sur chacune des dates générées. Les copies
                            sont créées en brouillon.
                        </FormLegend>

                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.fromDate"
                                label="Du"
                                field-name="fromDate"
                                field-id="fromDate"
                                type="date"
                                required
                            />

                            <InputField
                                v-model="form.toDate"
                                label="Au"
                                field-name="toDate"
                                field-id="toDate"
                                type="date"
                                required
                            />

                            <InputField
                                v-model="form.time"
                                label="Heure de publication"
                                field-name="time"
                                field-id="time"
                                type="time"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle>Jours de publication</FormTitle>

                        <SwitchField
                            v-for="weekDay in weekDays"
                            :key="weekDay.value"
                            v-model="form.weekDays[weekDay.value]"
                            :label="weekDay.label"
                            :field-name="`weekDay_${weekDay.value}`"
                            :field-id="`weekDay_${weekDay.value}`"
                            inline
                        />
                    </FormContent>
                    <FormContent>
                        <FormTitle>
                            {{ dates.length }} publication(s) seront créées
                        </FormTitle>

                        <FormLegend v-if="!dates.length">
                            Choisissez une période et au moins un jour de la
                            semaine pour générer les dates.
                        </FormLegend>

                        <ul
                            v-else
                            class="flex max-h-48 flex-col gap-1 overflow-y-auto text-sm text-gray-600"
                        >
                            <li v-for="date in dates" :key="date.getTime()">
                                {{ formatDate(date, false, true) }}
                            </li>
                        </ul>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    submit-text="Dupliquer"
                    submitting-text="Duplication en cours..."
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
